<template>
  <div>
    <div class="row page-title-header">
      <div class="col-12">
        <BreadCrumb
          parentRouteName="PointList"
          :extra-items="[
            '點數異動人員'
          ]" 
        />
      </div>
    </div>
    <b-card>
      <b-overlay :show="isLoading">
        <div class="row d-flex mb-4 mb-xl-2 justify-content-between">
          <h4 class="col-12 col-xl-4 mb-2 mb-xl-0 font-weight-bold">
            點數異動人員
          </h4>
          <div
            class="
              col-12 col-xl-8
              d-flex
              flex-xl-row
              align-items-start align-items-xl-center
              justify-content-end
            "
          >
            <b-button
              class="flex-shrink-0 mb-0 ml-2 btn"
              variant="primary"
              @click="handleExport"
              v-if="query.merchant_id"
            >
              <i class="fa fa-plus"></i>匯出 excel
            </b-button>
            <event-select
              v-model="query.event_id"
              class="ml-3"
              @change="handlePageReset"
            ></event-select>
            <b-form-select
              v-model="query.merchant_id"
              :options="merchants"
              :disabled="merchants.length == 2"
              @change="handlePageReset"
              class="ml-3"
            ></b-form-select>
            <b-input-group v-if="query.merchant_id">
              <b-form-input
                placeholder="搜尋會員名稱、手機、外部編碼"
                v-model="query.keyword"
                class="ml-3"
                @keyup.enter="handleSearch"
              ></b-form-input>
              <b-input-group-append>
                <b-button @click="handleSearch">
                  <i class="fa fa-search"></i>
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </div>
        </div>

        <b-table
          striped
          hover
          responsive
          :items="personalPoints"
          :fields="fields"
          :no-local-sorting="true"
          @sort-changed="handleSort"
        >
          <template #table-busy>
            <div class="text-center my-2">
              <b-spinner class="align-middle"></b-spinner>
            </div>
          </template>
          <template #cell(name)="data">
            {{ data.item.name | hiddenString(20) }}
          </template>
        </b-table>

        <CustomPagination
          :currentPage="query.page"
          :totalRows="total"
          :perPage="query.per_page"
          @page-change="handlePageChange"
          @per-page-change="handlePerPageChange"
        />
      </b-overlay>
    </b-card>
  </div>
</template>

<script>
import {mapGetters, mapState} from "vuex";
import personalPointApi from "@/apis/personal-point";
import merchantApi from "@/apis/merchant";
import store from "@/store";
import CustomPagination from "@/components/Page/Dashboard/CustomPagination.vue";
import EventSelect from "@/components/Dashboard/EventSelect";
import { updateQueryFromRoute, updateUrl } from "@/utils/updateUrl";
import * as consts from "@/consts";
import BreadCrumb from "@/components/Dashboard/BreadCrumb";

export default {
  components: { EventSelect, CustomPagination, BreadCrumb },
  data() {
    const baseFields = [
      {
        key: "name",
        label: "使用者名稱",
        sortable: true,
      },
      {
        key: "available_point",
        label: "可用點數",
      },
      {
        key: "accumulated_point",
        label: "累積點數",
      },
      {
        key: "used_point",
        label: "已使用點數",
      },
      {
        key: "expired_point",
        label: "過期點數",
      },
    ];
    return {
      isLoading: false,
      personalPoints: [],
      merchants: [{ value: null, text: "請選擇" }],
      total: 0,
      initialized: false,
      query: {
        page: 1,
        per_page: 10,
        event_id: null,
        keyword: "",
        sort_by: "name",
        order_by: "asc",
        merchant_id: null,
      },
      baseFields: baseFields,
      fields: baseFields,
    };
  },
  computed: {
    ...mapState("general", {
      organization: (state) => state.organization,
      defaultMerchantType: (state) => state.defaultMerchantType,
    }),
    queryWatcher() {
      return `${this.query.page}-${this.query.per_page}`;
    },
    filteredQuery() {
      return Object.fromEntries(
        // eslint-disable-next-line no-unused-vars
        Object.entries(this.query).filter(([key, value]) => value !== null)
        // eslint-enable-next-line no-unused-vars
      );
    },
    ...mapGetters({
      getModuleConfig: "dashboardModule/getConfig",
    }),
  },
  mounted() {
    this.fetchMerchants().then(() => {
      this.fetchPersonalPoints();
    });
    this.fetchFields();
    this.initialized = true;
  },
  async created() {
    this.query = updateQueryFromRoute(
      this.$route,
      this.$store,
      this.query
    );
  },
  watch: {
    queryWatcher: {
      handler() {
        if (!this.initialized) return;
        updateUrl(this.query, this.$store, this.$router);
      },
    },
    "$route.query": {
      handler(newQuery) {
        if (!this.initialized) return;
        if (Object.keys(newQuery).length !== 0) {
          this.query = { ...newQuery };
        }
        this.fetchPersonalPoints();
      },
      deep: true,
    },
  },
  methods: {
    async fetchPersonalPoints() {
      if (! this.query.merchant_id) {
        return;
      }

      try {
        this.isLoading = true;
        const params = {
          ...this.filteredQuery,
        };
        const { data } = await personalPointApi.getPersonalPoints(
          this.query.merchant_id,
          params
        );
        this.total = data.meta.total;
        this.personalPoints = data.data;
      } catch (error) {
        console.log(error);
      }
      this.isLoading = false;
    },
    async handleExport() {
      if (this.query.merchant_id) {
        const params = Object.fromEntries(
          Object.entries(this.query).filter(([key, value]) => value !== null && ! ['page', 'per_page'].includes(key))
        );
        const searchParams = new URLSearchParams({
          ...params,
          token: store.state.auth.token,
        });

        try {
          this.isLoading = true;

          await personalPointApi.exportPersonalPoints(this.query.merchant_id, searchParams)

          this.$swal({
            title: '成功',
            text: "可至“我的主控台”查看紀錄及下載檔案",
            showCancelButton: true,
            cancelButtonText: '確認',
            type: 'success',
            confirmButtonText: '前往查看',
          }).then((result)=>{
            if (result.value) {
              this.$router.push({
                name: "MyConsoleDownloadFileList",
                params: { org_code: this.$route.params.org_code },
              });
            }
          });
        } catch (e) {
          console.log(e)
        } finally {
          this.isLoading = false;
        }
      }
    },
    async fetchMerchants() {
      this.isLoading = true;
      const { data } = await merchantApi.list();

      this.merchants = [
        { value: null, text: "請選擇" },
        ...data.data.map((merchant) => {
          return {
            value: merchant.id,
            text: `${merchant.name} (${merchant.type_name})`,
          };
        }),
      ];

      if (!this.query.merchant_id) {
        const defaultMerchant = data.data.find((merchant) => merchant.type === this.defaultMerchantType) ??
          data.data.find((merchant) => merchant.type === consts.MERCHANT_TYPES.LINE_TYPE);

        if (defaultMerchant) {
          this.query.merchant_id = defaultMerchant.id;
        }
      }

      this.isLoading = false;
    },
    handleSort(ctx) {
      this.query.sort_by = ctx.sortBy;
      this.query.order_by = ctx.sortDesc ? "desc" : "asc";
      this.handlePageReset();
    },
    handleSearch() {
      if (this.query.merchant_id) {
        this.handlePageReset();
      }
    },
    handlePageChange(newPage) {
      this.query.page = newPage;
    },
    handlePerPageChange(newPerPage) {
      this.query.per_page = newPerPage;
      this.query.page = 1;
    },
    handlePageReset() {
      const currentPage = parseInt(this.query.page, 10);

      if (currentPage === 1) {
        updateUrl(this.query, this.$store, this.$router);
      } else {
        this.query.page = 1;
      }
    },
    async fetchFields() {
      let normalizeProfileFields = await this.normalizeProfileFields();
      const insertIndex = this.baseFields.findIndex(
        (field) => field.key === "name"
      );

      this.fields = [
        ...this.baseFields.slice(0, insertIndex + 1),
        ...normalizeProfileFields,
        ...this.baseFields.slice(insertIndex + 1),
      ];
    },
    async normalizeProfileFields() {
      const customerList = this.getModuleConfig("customer", "reward.customer_list");

      const profileMappings = customerList?.[0]?.member_fields ?? [];

      if (profileMappings) {
        return profileMappings.map((mapping) => {
          if (mapping.matched_key.startsWith("extra.")) {
            const key = mapping.matched_key.split("extra.")[1];
            return {
              key: `profiles.${key}`,
              label: mapping.title,
            };
          } else {
            return {
              key: mapping.matched_key,
              label: mapping.title,
            };
          }
        });
      } else {
        return [];
      }
    },
  },
};
</script>
